import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button.js';
import { EmptyState } from './EmptyState.js';
import { GamesGridSkeleton } from './GamesGridSkeleton.js';
import { ProviderArt } from './ProviderArt.js';
import { fetchAllGames, fetchProviders, GAMES_QUERY_KEYS } from '@/features/games/api.js';
import { providerBadgeCodeOf } from '@/features/games/brandBadge.js';
import { providersOf } from '@/features/games/providersOf.js';

/**
 * A 3:4 art card per camp that carries slot games, like a game card; each links to that camp's
 * slot list on /casino. Owns its fetch + loading/error/empty states, so mount
 * it only where it shows — it pulls the whole slot catalog to know which camps
 * have slots.
 */
export function SlotProviderGrid() {
  const { t } = useTranslation(['casino', 'common']);
  const slotGames = useQuery({
    queryKey: [...GAMES_QUERY_KEYS.list({ gameType: 'slot' }), 'all'],
    queryFn: () => fetchAllGames({ gameType: 'slot' }),
    staleTime: 60_000,
  });
  const providersQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.providers,
    queryFn: fetchProviders,
    staleTime: 60_000,
  });
  const providers = useMemo(
    () => providersOf(slotGames.data ?? [], providersQuery.data?.items ?? []),
    [slotGames.data, providersQuery.data],
  );

  if (slotGames.isPending) return <GamesGridSkeleton count={6} />;
  if (slotGames.isError) {
    return (
      <EmptyState
        icon="rotate"
        title={t('casino:loadError.title')}
        body={t('casino:loadError.body')}
        action={
          <Button size="sm" variant="secondary" onClick={() => void slotGames.refetch()}>
            {t('common:common.retry')}
          </Button>
        }
      />
    );
  }
  if (providers.length === 0) return <EmptyState title={t('casino:empty.title')} body={t('casino:empty.body')} />;

  return (
    <ul className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
      {providers.map((p) => (
        <li key={p.id}>
          <Link
            to="/casino"
            search={{ gameType: 'slot', providerId: p.id }}
            className="group block rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] focus-visible:ring-[var(--primary)]"
          >
            <ProviderArt
              name={p.name}
              logoUrl={p.logoUrl}
              brandColor={p.brandColor}
              badgeCode={providerBadgeCodeOf(p.code)}
              nameClassName="text-[15px] md:text-lg"
              className="aspect-[3/4] rounded-2xl ring-1 ring-[var(--border-strong)] transition-[transform,box-shadow] duration-200 ease-out group-hover:scale-[1.04] group-hover:ring-[var(--accent)] group-hover:shadow-[0_6px_18px_var(--accent-glow)]"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}
